<script>
	import { resolve } from '$app/paths';
	import ApolloPixel from '$lib/components/apollo-pixel.svelte';
	import CookieBanner from '$lib/components/cookie-banner.svelte';
	import MarketingNavbar from '$lib/components/marketing-navbar.svelte';
	import ThemeSwitcher from '$lib/components/theme-switcher.svelte';
	import Github from '$lib/icons/github.svelte';
	import HessianIcon from '$lib/icons/hessian-icon.svelte';
	import Linkedin from '$lib/icons/linkedin.svelte';
	import { cookieConsent } from '$lib/utils/cookieConsent.svelte';
	import { ModeWatcher } from 'mode-watcher';
	import '../app.css';

	let { children } = $props();

	const title = "Hessian — Automate the operations your team can't scale";
	const description =
		'We forward deploy with you to apply agents to your use-cases, using your existing tooling.';
	const url = 'https://hessian.sh';
	const image = `${url}/og-image.png`;
	const imageAlt = 'Hessian — agentic workflow platform';
	const twitterHandle = '@hessianhq';

	// JSON-LD structured data — surfaces the Organization in Google's knowledge
	// panel and gives crawlers a machine-readable handle on the brand + socials.
	const orgJsonLd = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Hessian',
		url,
		logo: `${url}/logo.png`,
		description,
		sameAs: ['https://www.linkedin.com/company/hessianhq/', 'https://github.com/hessianhq']
	});
	const softwareJsonLd = JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		name: 'Hessian',
		applicationCategory: 'BusinessApplication',
		operatingSystem: 'Web, Self-hosted',
		url,
		description
	});
</script>

<svelte:head>
	<title>{title}</title>
	<meta name="description" content={description} />
	<meta name="robots" content="index, follow" />

	<!-- Open Graph -->
	<meta property="og:type" content="website" />
	<meta property="og:title" content={title} />
	<meta property="og:description" content={description} />
	<meta property="og:image" content={image} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content={imageAlt} />
	<meta property="og:url" content={url} />
	<meta property="og:site_name" content="Hessian" />
	<meta property="og:locale" content="en_US" />

	<!-- Twitter/X -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:site" content={twitterHandle} />
	<meta name="twitter:creator" content={twitterHandle} />
	<meta name="twitter:title" content={title} />
	<meta name="twitter:description" content={description} />
	<meta name="twitter:image" content={image} />
	<meta name="twitter:image:alt" content={imageAlt} />

	<!-- DNS prefetch for third-party hosts hit during render -->
	<link rel="dns-prefetch" href="https://img.logo.dev" />
	<link rel="dns-prefetch" href="https://calendar.app.google" />

	<!-- Other -->
	<link rel="canonical" href={url} />
	<meta name="theme-color" content="#000000" />
	<link rel="apple-touch-icon" href={`${url}/favicon.png`} />

	<!-- Structured data — Organization + SoftwareApplication for rich results. -->
	{@html `<script type="application/ld+json">${orgJsonLd}</script>`}
	{@html `<script type="application/ld+json">${softwareJsonLd}</script>`}
</svelte:head>

<ModeWatcher />

<!-- Subtle vertical guide rails marking the content area — fixed so they
	 span the viewport regardless of scroll position. z-index: -10 keeps them
	 BEHIND all in-flow content so any section bg (white, sunken, etc.)
	 naturally masks them; transparent sections still show them through. -->
<div
	class="pointer-events-none fixed inset-0 z-[-10] hidden px-6 sm:block sm:px-10"
	aria-hidden="true"
>
	<div class="border-border/40 dark:border-border/80 mx-auto h-full max-w-264 border-x"></div>
</div>

<MarketingNavbar />

{@render children()}

<ApolloPixel />
<CookieBanner />

<footer class="border-border">
	<div
		class="mx-auto flex max-w-264 flex-col items-start gap-8 px-10 pt-14 pb-12 sm:flex-row sm:items-center sm:justify-between sm:px-20 lg:px-28"
	>
		<!-- Brand: logo with copyright stacked below. -->
		<div class="flex flex-col gap-2.5">
			<a href={resolve('/')} class="text-primary inline-flex items-center gap-2">
				<HessianIcon class="fill-primary size-6" />
				<span class="text-lg leading-[1.1] font-medium tracking-tighter">Hessian</span>
			</a>
			<span class="text-soft-gray text-xs">
				© {new Date().getFullYear()} Hessian Inc. <span aria-hidden="true">·</span> San Francisco
			</span>
		</div>

		<!-- Right cluster: attribution, legal, socials, theme toggle. -->
		<div class="text-soft-gray flex flex-wrap items-center gap-6">
			<a
				href="https://logo.dev"
				target="_blank"
				rel="noopener noreferrer"
				class="hover:text-primary text-xs transition-colors"
			>
				Logos provided by Logo.dev
			</a>

			<a href={resolve('/legal/terms')} class="hover:text-primary text-xs transition-colors">
				Terms
			</a>

			<a href={resolve('/legal/privacy')} class="hover:text-primary text-xs transition-colors">
				Privacy
			</a>

			<button
				type="button"
				onclick={() => cookieConsent.reopen()}
				class="hover:text-primary cursor-pointer text-xs transition-colors"
			>
				Cookie preferences
			</button>

			<div class="flex items-center gap-4">
				<a
					href="https://www.linkedin.com/company/hessianhq/"
					aria-label="Hessian on LinkedIn"
					target="_blank"
					rel="noopener noreferrer"
					class="hover:text-primary inline-flex size-6 transition-colors duration-200"
				>
					<Linkedin />
				</a>
				<a
					href="https://github.com/hessianhq"
					aria-label="Hessian on GitHub"
					target="_blank"
					rel="noopener noreferrer"
					class="hover:text-primary inline-flex size-6 transition-colors duration-200"
				>
					<Github />
				</a>
			</div>
			<ThemeSwitcher class="mb-0" />
		</div>
	</div>
</footer>
